import { createSignal, splitProps } from "solid-js";
import { Select } from "~/components/Forms/Fields/Base/Select";

import type {
  InputOption,
  SchemaOptions,
  SelectPropsWithOptionalOptions,
  ValidationFunc,
} from "~/components/Forms/Fields/Base/InputTypesProps";
import { picklist, safeParse } from "valibot";
import ValidationErrors from "./Base/ValidationErrors";

const LABEL = "Région recherchée";
const HELP = undefined;
const IS_REQUIRED_BY_DEFAULT = true;

const fr = "France entière";
const annecy = "Annecy";
const angers = "Angers";
const bayonne = "Bayonne";
const caen = "Caen";
const bordeaux = "Bordeaux";
const grenoble = "Grenoble";
const iledefrance = "Île-de-France";
const paris = "Paris (75)";
const seineetmarne = "Seine-et-Marne (77)";
const yvelines = "Yvelines (78)";
const essonne = "Essonne (91)";
const hautdeseine = "Hauts-de-Seine (92)";
const seineetsaintdenis = "Seine-Saint-Denis (93)";
const valdemarne = "Val-de-Marne (94)";
const valdoise = "Val-d'Oise (95)";
const lille = "Lille";
const lyon = "Lyon";
const marseille = "Marseille";
const montpellier = "Montpellier";
const nantes = "Nantes";
const nice = "Nice";
const quimper = "Quimper";
const rouen = "Rouen";
const strasbourg = "Strasbourg";
const toulouse = "Toulouse";
const tours = "Tours";

const etsaregion = " et sa région";
const tiret = "- ";

export const availableOptions: InputOption[] = [
  {
    label: fr,
    value: fr,
    name: fr,
  },
  {
    label: angers + etsaregion,
    value: angers + etsaregion,
    name: angers + etsaregion,
  },
  {
    label: annecy + etsaregion,
    value: annecy + etsaregion,
    name: annecy + etsaregion,
  },
  {
    label: bayonne + etsaregion,
    value: bayonne + etsaregion,
    name: bayonne + etsaregion,
  },
  {
    label: bordeaux + etsaregion,
    value: bordeaux + etsaregion,
    name: bordeaux + etsaregion,
  },
  {
    label: caen + etsaregion,
    value: caen + etsaregion,
    name: caen + etsaregion,
  },
  {
    label: grenoble + etsaregion,
    value: grenoble + etsaregion,
    name: grenoble + etsaregion,
  },
  {
    label: iledefrance,
    value: iledefrance,
    name: iledefrance,
  },
  {
    label: tiret + paris,
    value: paris,
    name: tiret + paris,
  },
  {
    label: tiret + seineetmarne,
    value: seineetmarne,
    name: tiret + seineetmarne,
  },
  {
    label: tiret + yvelines,
    value: yvelines,
    name: tiret + yvelines,
  },
  {
    label: tiret + essonne,
    value: essonne,
    name: tiret + essonne,
  },
  {
    label: tiret + hautdeseine,
    value: hautdeseine,
    name: tiret + hautdeseine,
  },
  {
    label: tiret + seineetsaintdenis,
    value: seineetsaintdenis,
    name: tiret + seineetsaintdenis,
  },
  {
    label: tiret + valdemarne,
    value: valdemarne,
    name: tiret + valdemarne,
  },
  {
    label: tiret + valdoise,
    value: valdoise,
    name: tiret + valdoise,
  },
  {
    label: lille + etsaregion,
    value: lille + etsaregion,
    name: lille + etsaregion,
  },
  {
    label: lyon + etsaregion,
    value: lyon + etsaregion,
    name: lyon + etsaregion,
  },
  {
    label: marseille + etsaregion,
    value: marseille + etsaregion,
    name: marseille + etsaregion,
  },
  {
    label: montpellier + etsaregion,
    value: montpellier + etsaregion,
    name: montpellier + etsaregion,
  },
  {
    label: nantes + etsaregion,
    value: nantes + etsaregion,
    name: nantes + etsaregion,
  },
  {
    label: nice + etsaregion,
    value: nice + etsaregion,
    name: nice + etsaregion,
  },
  {
    label: quimper + etsaregion,
    value: quimper + etsaregion,
    name: quimper + etsaregion,
  },
  {
    label: rouen + etsaregion,
    value: rouen + etsaregion,
    name: rouen + etsaregion,
  },
  {
    label: strasbourg + etsaregion,
    value: strasbourg + etsaregion,
    name: strasbourg + etsaregion,
  },
  {
    label: toulouse + etsaregion,
    value: toulouse + etsaregion,
    name: toulouse + etsaregion,
  },
  {
    label: tours + etsaregion,
    value: tours + etsaregion,
    name: tours + etsaregion,
  },
] as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function DesiredAreaSchema(options?: SchemaOptions) {
  return {
    desired_area: picklist(
      availableOptions.map((o) => o.value),
      "Veuillez sélectionner une région",
    ),
  };
}

export function DesiredArea(props: SelectPropsWithOptionalOptions) {
  const [local, others] = splitProps(props, ["label", "help", "required"]);
  const [validationErrors, setValidationErrors] = createSignal<string[]>([]);

  const validate: ValidationFunc = (input) => {
    const ret = safeParse(DesiredAreaSchema().desired_area, input);
    ret.success
      ? setValidationErrors([])
      : setValidationErrors(ret.issues.map((issue) => issue.message));
  };

  return (
    <>
      <div class={`form-item-${props.name}`}>
        <Select
          label={local.label || LABEL}
          help={local.help || HELP}
          validateFn={validate}
          required={
            local.required !== undefined
              ? local.required
              : IS_REQUIRED_BY_DEFAULT
          }
          options={availableOptions}
          {...others}
        />
        <ValidationErrors errors={validationErrors()} field={props.name} />
      </div>
    </>
  );
}
